import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { createStore, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import MediaCaptureProvider from './MediaCaptureProvider';
import CapturePresentation from './CapturePresentation';
import MediaContainer from './MediaContainer';
import MediaOverlay from './MediaOverlay';
import CapturedMedia from './CapturedMedia';
import Controller from './Controller';
import CTA from './CTA';
import { reducer, getInitialState } from '../../reducers';
import { applyTranslations, TranslationPropTypes } from '../../constants/translated/translations';
var TranslationsPropType = PropTypes.shape(TranslationPropTypes);

var MediaCapture = /*#__PURE__*/function (_Component) {
  _inherits(MediaCapture, _Component);

  function MediaCapture(props) {
    var _this;

    _classCallCheck(this, MediaCapture);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(MediaCapture).call(this, props));
    var initialState = getInitialState(_this.props.onCompleted);
    _this.state = {
      store: createStore(reducer, initialState, applyMiddleware(thunk))
    };
    applyTranslations(_this.props.translations);
    return _this;
  }

  _createClass(MediaCapture, [{
    key: "render",
    value: function render() {
      return React.createElement(MediaCaptureProvider, {
        onClose: this.props.onClose,
        store: this.state.store,
        render: function render(_ref) {
          var _ref$state = _ref.state,
              captureState = _ref$state.captureState,
              msg = _ref$state.msg,
              videoSrc = _ref$state.videoSrc,
              videoDeviceId = _ref$state.videoDeviceId,
              audioDeviceId = _ref$state.audioDeviceId,
              soundMeter = _ref$state.soundMeter,
              devices = _ref$state.devices,
              fileName = _ref$state.fileName,
              hasStarted = _ref$state.hasStarted,
              requestAudioOnly = _ref$state.requestAudioOnly,
              actions = _ref.actions;
          return React.createElement(CapturePresentation, null, React.createElement(MediaContainer, null, React.createElement(MediaOverlay, {
            captureState: captureState,
            soundMeter: soundMeter,
            msg: msg,
            actions: actions
          }), React.createElement(CapturedMedia, {
            captureState: captureState,
            videoSrc: videoSrc,
            fileName: fileName,
            devices: devices,
            audioDeviceId: audioDeviceId,
            videoDeviceId: videoDeviceId,
            requestAudioOnly: requestAudioOnly,
            actions: actions
          })), React.createElement(Controller, {
            captureState: captureState,
            fileName: fileName,
            devices: devices,
            audioDeviceId: audioDeviceId,
            videoDeviceId: videoDeviceId,
            actions: actions
          }, React.createElement(CTA, {
            captureState: captureState,
            hasStarted: hasStarted,
            actions: actions
          })));
        }
      });
    }
  }]);

  MediaCapture.displayName = "MediaCapture";
  return MediaCapture;
}(Component);

MediaCapture.propTypes = {
  /**
   * Callback fired when a file has been generated.
   */
  onCompleted: PropTypes.func.isRequired,

  /**
   * Callback fired when the component is closed for a reason
   * other than generating a file. Includes the last
   * emitted state when the action was taken.
   */
  onCancel: PropTypes.func,

  /**
   * Callback fired when the component is closed. Includes
   * the last emitted state when the action was taken.
   */
  onClose: PropTypes.func,

  /**
   * Label overrides for i18n. Defaults to english
   * See src/constants/translated/translations.js for default values
   */
  translations: TranslationsPropType
};
MediaCapture.defaultProps = {
  onCancel: function onCancel() {},
  onClose: function onClose() {},
  translations: {}
};
export default MediaCapture;